import { memo, useEffect, useState, MouseEvent } from 'react'
import { Menu, Grid } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { FILTER_LIST } from 'components/Reporting/Overview/FilterBuilder/List'
import FilterWrapper from 'components/Shared/FilterBuilder/FilterWrapper'
import FilterMenus from 'components/Reporting/Overview/FilterBuilder/FilterMenus'
import AddFilterButton from 'components/Shared/FilterBuilder/AddFilterButton'
import ClearAllFiltersButton from 'components/Shared/FilterBuilder/ClearAllFiltersButton'
import FilterSet from 'components/Reporting/Overview/FilterBuilder/FilterSet'
import { AdminUITheme } from 'types/AdminUiThemeMui'
import { setFilters, resetFilters } from 'redux/slices/FilterBuilder'
import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { isAuthorized } from 'lib/authorization/AuthorizeComponent'
import { isMobileView } from 'lib/GlobalConstants'
import {
  filterWithComponent,
  toggleFeature,
  getDemographicOutput,
  filterListAllowed,
  defaultFilterset,
} from 'components/Reporting/Overview/FilterBuilder/Helper'

const FilterBuilder = ({
  onClearFilter,
  onSaveFilterSet,
  handleMenuItemClick,
  handleRenameSelection,
  handleDeleteSelection,
  selectedIndex,
  filterSetResult,
}: {
  onClearFilter: () => void
  onSaveFilterSet?: (
    inputRef: any,
    selectedFilters: any,
    savedFilterProps: any,
  ) => void
  filterSetResult?: any
  handleMenuItemClick?: (event: any, index: number) => void
  handleRenameSelection?: (
    filterId: string,
    filterName: string,
    inputRef: any,
  ) => void
  handleDeleteSelection?: (event: any, index: number) => void
  selectedIndex?: number
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [activeDemographic, setActiveDemographic] = useState<any>({})
  const open = Boolean(anchorEl)
  const theme: AdminUITheme = useTheme()
  const dispatch = useAppDispatch()
  const {
    age: demographicAge = false,
    ethnicity: demographicEthnicity = false,
    gender: demographicGender = false,
  } = activeDemographic || {}

  const showProjectFilters = isAuthorized('enable_reporting_filter_builder')
  const showYOBFilter = demographicAge || false
  const showEthnicityFilter = demographicEthnicity || false
  const showGenderFilter = demographicGender || false
  const showLocationFilter = isAuthorized('location_question') || false

  const LIST_FILTERS_APPLIED = FILTER_LIST.filter(
    (filter: any) =>
      toggleFeature(filter, showYOBFilter, 'yearOfBirth') &&
      toggleFeature(filter, showEthnicityFilter, 'ethnicity') &&
      toggleFeature(filter, showGenderFilter, 'gender') &&
      toggleFeature(filter, showLocationFilter, 'dashboardLocation'),
  )

  const allowedFilterList = filterListAllowed(LIST_FILTERS_APPLIED)

  useEffect(() => {
    const defaultFilters = defaultFilterset(FILTER_LIST, allowedFilterList)
    dispatch(setFilters(defaultFilters))
    try {
      getDemographicOutput().then(
        (res: any) => res && setActiveDemographic(res),
      )
    } catch (err: any) {
      return err.errors
    }
  }, [])

  const {
    filterBuilder: {
      selectedFilters,
      savedSelectionFilters,
      savedFilter,
      isFiltersetVisible,
    },
    features: {
      features: {
        enable_participant_page_hub_filters: { enabled = false } = {},
      } = {},
    } = {},
  } = useAppSelector((state) => state)

  const handleAddFilterClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClearFilter = () => {
    dispatch(resetFilters())
    onClearFilter?.()
  }

  const showSelectedFiltersOnUI = () => {
    const mobileWidth = isMobileView ? { width: '100%' } : {}
    return selectedFilters.map((filter: any, index: number) => {
      if (!filter.component) return

      return (
        <Grid
          item
          key={index}
          style={{
            ...theme.ParticipantGrid.selectedFilterWidth,
            ...mobileWidth,
          }}
          display={
            filter.name === 'dateRange' || !isFiltersetVisible ? 'flex' : 'none'
          }
        >
          {filterWithComponent(
            filter,
            index,
            savedSelectionFilters,
            savedFilter,
          )}
        </Grid>
      )
    })
  }

  const showFilterData = () => {
    return (
      <Grid
        item
        {...(isMobileView && { xs: 12 })}
        display={!isFiltersetVisible ? 'flex' : 'none'}
        alignItems="center"
      >
        {showProjectFilters && (
          <>
            <AddFilterButton handleAddFilterClick={handleAddFilterClick} />
            <FilterSet
              filterSetName="saveBtn"
              onSaveFilterSet={onSaveFilterSet}
              selectedIndex={selectedIndex}
            />
            <ClearAllFiltersButton
              handleClearFilter={handleClearFilter}
              isDisabled={
                savedSelectionFilters?.length
                  ? false
                  : selectedFilters.length === 1
              }
            />
          </>
        )}
        <Menu anchorEl={anchorEl} open={open} onClose={() => handleClose()}>
          <FilterMenus filters={allowedFilterList} {...filterMenuProps} />
        </Menu>
      </Grid>
    )
  }

  const filterMenuProps = { handleClose }

  return (
    <Grid container sx={{ mb: 3 }}>
      <Grid item xs={12}>
        <FilterWrapper>
          <>
            {enabled ? (
              <Grid
                item
                {...(isMobileView && { xs: 12 })}
                style={{
                  ...theme.ParticipantGrid.selectedFilterWidth,
                }}
              >
                <FilterSet
                  filterSetName="buttonGroup"
                  filterSetResult={filterSetResult}
                  handleMenuItemClick={handleMenuItemClick}
                  handleRenameSelection={handleRenameSelection}
                  handleDeleteSelection={handleDeleteSelection}
                  selectedIndex={selectedIndex}
                />
              </Grid>
            ) : null}

            {showSelectedFiltersOnUI()}
            {showFilterData()}
          </>
        </FilterWrapper>
      </Grid>
    </Grid>
  )
}

export default memo(FilterBuilder)
